<template>
  <v-app>
    <v-navigation-drawer
      v-model="showDrawer"
      v-if="isAuthenticatedRoute"
      app
      temporary
      color="white"
      class="hide-on-print"
    >
      <v-row>
        <v-col cols="12" align="center">
          <img
            class="logo my-3"
            :src="logo"
            alt="StaffLynx"
          >
        </v-col>
      </v-row>
      <v-divider class="mx-5 mb-3"></v-divider>
      <v-list dense>
        <div :key="item.text" v-for="item in menuFirsSectionItems">
          <MenuItem :item="item"/>
        </div>
        <v-divider
          class="mx-5 my-3"
          v-if="(menuSecondSectionItems && menuSecondSectionItems.length > 0) ||
          (placementsMenuItems && placementsMenuItems.length > 0) ||
          (goalsMenuItems && goalsMenuItems.length > 0) ||
          (menuReportsItem && menuReportsItem.length > 0)"
        ></v-divider>
        <div :key="item.text" v-for="item in menuSecondSectionItems">
          <MenuItem :item="item"/>
        </div>
        <MenuItemGroup
          icon='assignment_ind'
          title='Placements'
          :items="placementsMenuItems"
          class="my-3"
          v-if="placementsMenuItems && placementsMenuItems.length > 0"
        />
        <MenuItemGroup
          icon='flag'
          title='Goals'
          :items="goalsMenuItems"
          class="my-3"
          v-if="goalsMenuItems && goalsMenuItems.length > 0"
        />
        <MenuItemGroup
          icon='assessment'
          title='Reports'
          :items="menuReportsItem"
          class="my-3"
          v-if="menuReportsItem && menuReportsItem.length > 0"
        />
        <v-divider class="mx-5 my-3"></v-divider>
        <MenuItemGroup
          icon='schedule'
          title='Time'
          :items="timeMenuItems"
          class="my-3"
          v-if="timeMenuItems && timeMenuItems.length > 0"
        />
        <div :key="item.text" v-for="item in menuThirdSectionItems">
          <MenuItem :item="item"/>
        </div>
        <MenuItem :item="menuLogoutItem"/>
      </v-list>
    </v-navigation-drawer>
    <v-app-bar
      app
      color="blue darken-3"
      dark
      class="hide-on-print"
    >
      <v-app-bar-nav-icon
        v-if="isAuthenticatedRoute"
        @click.stop="toggleDrawer"
      />
      <img
        v-if="isAuthenticatedRoute"
        class="white-logo ml-3"
        :src="whiteLogo"
        alt="White StaffLynx Logo"
        @click="goHome"
      >
      <v-spacer></v-spacer>
      <div
        class="shine mr-3"
        v-if="isAuthenticatedRoute && showWeHaveClosedADealButton"
      >
        <button class="gold-background closed-deal-button" @click="goToDealWizard">
          <span class="closed-deal-button-text font-weight-bold mx-3">{{ weHaveClosedADealLabel }}</span>
          <i></i>
        </button>
      </div>
      <UserDisplayName
        :isAuthenticatedRoute="isAuthenticatedRoute"
        :currentUserDisplayName="currentUserDisplayName"
        @logout-clicked="handleLogoutClick"
        @my-profile-clicked="handleMyProfileClick"
      />
    </v-app-bar>
    <v-main fluid class="body-content align-center">
      <router-view/>
      <NotificationsSnackBar/>
      <ProgressModal/>
    </v-main>
    <v-bottom-navigation
      color="#e6e6e6"
      height="30"
      class="hide-on-print"
    >
      <div style="margin: auto">
        <a
          href="https://cticonsulting.net/"
          style="text-decoration: none;"
          target="_blank"
        >
          <span style="color: black">&copy; CTI Consulting - {{ new Date().getFullYear() }}</span>
        </a>
      </div>
    </v-bottom-navigation>
  </v-app>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { NotificationsSnackBar } from '@/components/notifications'
import { ProgressModal } from '@/components/dialogs'
import UserDisplayName from './UserDisplayName'
import logo from '@/assets/logo.png'
import whiteLogo from '@/assets/whiteLogo.png'
import { MenuItem, MenuItemGroup } from '@/components/listItems'
import {
  usersRoute,
  staffRoute,
  clientsRoute,
  assignmentsRoute,
  consultantTimeSheetRoute,
  logoutRoute,
  loginRoute,
  timeCardReportRoute,
  myProfileRoute,
  directPlacementsRoute,
  commissionReportRoute,
  commissionDetailsReportRoute,
  invoiceReportRoute,
  payrollReportRoute,
  gpToCostReportRoute,
  dealsRoute,
  userAdminTodosRoute,
  userTodoDashboardRoute,
  teamsRoute,
  settingsRoute,
  placementGoalsRoute,
  directPlacementGoalsRoute,
  revenueDataRoute,
  dashboardRoute,
  dealWizardRoute,
  clockLogsRoute,
  clockLogsReportRoute,
  timeManagementRoute
} from '@/lib/constants/routes'
import { userHasRoles, getCurrentUser, isAuthTokenValid } from '@/lib/authentication'
import { requiresAuthentication } from '@/lib/routes'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { AUTH_USER_KEY } from '@/lib/constants/authentication'

export default {
  name: 'AppAuthenticated',
  components: {
    NotificationsSnackBar,
    UserDisplayName,
    ProgressModal,
    MenuItem,
    MenuItemGroup
  },
  data: () => ({
    logo,
    whiteLogo,
    tree: [],
    menuLogoutItem: { icon: 'exit_to_app', text: 'Log Out', path: logoutRoute.path },
    menuFirsSectionItems: [],
    menuSecondSectionItems: [],
    menuThirdSectionItems: [],
    placementsMenuItems: [],
    goalsMenuItems: [],
    menuReportsItem: [],
    timeMenuItems: [],
    currentUserSource: null
  }),
  computed: {
    ...mapFields('application', [
      'showDrawer'
    ]),
    isAuthenticatedRoute () {
      const isAuthenticatedRoute = this.$route.matched.some(requiresAuthentication)

      return isAuthenticatedRoute
    },
    currentUserDisplayName () {
      return this.currentUser ? this.currentUser.firstName : null
    },
    currentUser: {
      get () {
        let userInCache = localStorage.getItem(AUTH_USER_KEY)
        return this.currentUserSource ? this.currentUserSource : (userInCache ? JSON.parse(userInCache) : null)
      },
      set (newValue) {
        this.currentUserSource = newValue
      }
    },
    weHaveClosedADealLabel () {
      return this.$vuetify.breakpoint.xs ? '$$$' : 'We Have Closed a Deal'
    },
    showWeHaveClosedADealButton () {
      return userHasRoles({ user: this.currentUser, allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    }
  },
  methods: {
    ...mapActions('authentication', [
      'logout'
    ]),
    ...mapActions('application', [
      'toggleDrawer'
    ]),
    goHome (event) {
      const routeData = this.$router.resolve({
        name: dashboardRoute.name
      })
      if (event.ctrlKey === true) {
        window.open(routeData.href, '_blank')
      } else {
        window.open(routeData.href, '_self')
      }
    },
    getMenuFirstSection () {
      return [
        { icon: 'home', text: 'Home', route: dashboardRoute },
        { icon: 'person', text: 'Users', route: usersRoute },
        { icon: 'group', text: 'Internal Employees', route: staffRoute },
        { icon: 'groups', text: 'Teams', route: teamsRoute },
        { icon: 'apartment', text: 'Clients', route: clientsRoute },
        { icon: 'task_alt', text: 'Task Management', route: userAdminTodosRoute },
        { icon: 'settings', text: 'Settings', route: settingsRoute }
      ]
    },
    getMenuSecondSection () {
      return [
        { icon: 'handshake', text: 'Deals', route: dealsRoute }
      ]
    },
    getMenuThirdSection () {
      return [
        { icon: 'task_alt', text: 'My Tasks', route: userTodoDashboardRoute },
        { icon: 'av_timer', text: 'Time Sheet', route: consultantTimeSheetRoute },
        { icon: 'av_timer', text: 'Clock In/Clock Out', route: clockLogsRoute }
      ]
    },
    getPlacementsMenuItems () {
      return [
        { icon: 'work', text: 'Perm. Placements', route: directPlacementsRoute },
        { icon: 'badge', text: 'Contracts', route: assignmentsRoute }
      ]
    },
    getGoalsMenuItems () {
      return [
        { icon: 'work', text: 'Perm. Placements', route: directPlacementGoalsRoute },
        { icon: 'badge', text: 'Contracts', route: placementGoalsRoute },
        { icon: 'account_balance', text: 'Revenue', route: revenueDataRoute }
      ]
    },
    getMenuReports () {
      return [
        { icon: 'schedule', text: 'Time Card', route: timeCardReportRoute },
        { icon: 'request_quote', text: 'Invoicing', route: invoiceReportRoute },
        { icon: 'payments', text: 'Payroll', route: payrollReportRoute },
        { icon: 'query_stats', text: 'GP vs Cost', route: gpToCostReportRoute },
        { icon: 'attach_money', text: 'Commission', route: commissionReportRoute },
        { icon: 'paid', text: 'Commission Details', route: commissionDetailsReportRoute }
      ]
    },
    getTimeMenuItems () {
      return [
        { icon: 'event_note', text: 'Clock Details', route: clockLogsReportRoute },
        { icon: 'calendar_month', text: 'Time Management', route: timeManagementRoute }
      ]
    },
    handleLogoutClick () {
      this.logout().then(() => {
        this.$router.push(loginRoute.url)
        this.$store.replaceState(JSON.parse(JSON.stringify(this.$initialStateCopy)))
      })
    },
    handleMyProfileClick () {
      this.$router.push(myProfileRoute.url)
    },
    getMenuItems (items) {
      if (!this.currentUser || !this.currentUser.id) {
        return []
      }
      return items
        .filter(menuItem => userHasRoles({
          user: this.currentUser,
          allowedRoles: menuItem.route.roles,
          hiddenRoles: menuItem.hiddenRoles
        }))
        .map(menuItem =>
          ({ icon: menuItem.icon, text: menuItem.text, path: menuItem.route.url })
        )
    },
    goToDealWizard (event) {
      const routeData = this.$router.resolve({
        name: dealWizardRoute.name
      })
      if (event.ctrlKey === true) {
        window.open(routeData.href, '_blank')
      } else {
        window.open(routeData.href, '_self')
      }
    }
  },
  beforeUpdate () {
    this.currentUser = getCurrentUser()
    this.menuFirsSectionItems = this.getMenuItems(this.getMenuFirstSection())
    this.menuSecondSectionItems = this.getMenuItems(this.getMenuSecondSection())
    this.menuThirdSectionItems = this.getMenuItems(this.getMenuThirdSection())
    this.placementsMenuItems = this.getMenuItems(this.getPlacementsMenuItems())
    this.goalsMenuItems = this.getMenuItems(this.getGoalsMenuItems())
    this.menuReportsItem = this.getMenuItems(this.getMenuReports())
    this.timeMenuItems = this.getMenuItems(this.getTimeMenuItems())
  },
  created () {
    if (!isAuthTokenValid() && requiresAuthentication(this.$route)) {
      this.$router.push({ name: loginRoute.name })
    }
  }
}
</script>

<style>
.vertical-center {
  margin: 0;
  position: absolute;
  top: 50%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
}

.title-style {
  font-family: 'Raleway', cursive !important;
}
.cardRoundedStyle {
  background-color: white !important;
  border-radius: 15px !important;
}
.body-content {
  background-image: linear-gradient(#ffffff, #f2f2f2);
}

.yellow-brand {
  color: #e0b70e !important;
}

.text-white {
  color: white !important;
}

.text-black {
  color: black !important;
}

.logo {
  height: auto;
  width: 130px;
}

.white-logo {
  height: auto;
  width: 80px;
}

.white-logo:hover {
  cursor: pointer;
}

.abs-left-0 {
  position: absolute;
  left: 0px;
}

.print-only {
  display: none;
}

.v-data-table--dense th {
  padding: 20px !important;
}

@media only screen and (max-width: 959px) {
  .hide-on-sm {
    display: none !important;
  }

  .fixed-buttons {
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: #fff;
    z-index: 1;
    box-shadow: 0px 0px 15px 0px #888888;
  }
}

@media only screen and (min-width: 959px) {
  .hide-on-md {
    display: none !important;
  }
}

@media print {
  .v-data-table.tableParent {
    background: #f5f5f5 !important;
  }

  .remove-link-on-print {
    text-decoration: none;
    color: inherit;
  }

  .hide-on-print {
    display: none !important;
  }

  .print-only {
    display: initial;
  }

  /*  Header App */
  nav.v-toolbar.v-toolbar--clipped.v-toolbar--fixed.theme--dark.blue.darken-3 {
    display: none;
  }

  nav.v-toolbar.v-toolbar--fixed.theme--dark.blue.darken-3 {
    display: none;
  }

  /*  Header Menu List*/
  aside.v-navigation-drawer.v-navigation-drawer--fixed.v-navigation-drawer--is-mobile.v-navigation-drawer--open.theme--light {
    display: none;
  }

  aside.v-navigation-drawer.v-navigation-drawer--clipped.v-navigation-drawer--fixed.v-navigation-drawer--open.theme--light {
    display: none;
  }

  /*  Header Menu List*/
  main.v-content {
    padding: 20px !important;
  }

  .v-overlay.v-overlay--active {
    display: none;
  }

  /*Print View*/
  .print-ma-0 {
    margin: 0;
  }

  .print-pa-0 {
    padding: 0;
  }

  .print-my-0 {
    margin-top: 0;
    margin-bottom: 0;
  }

  .print-mx-0 {
    margin-right: 0;
    margin-left: 0;
  }

  .print-pa-0 {
    padding: 0;
  }

  .print-py-0 {
    padding-top: 0;
    padding-bottom: 0;
  }

  .print-px-0 {
    padding-right: 0;
    padding-left: 0;
  }

  .print-withoutoverflow {
    overflow: initial !important;
  }

  /*Report*/
  .report-data-header > div > div > div {
    border-top: 1px solid #9e9e9e !important;
    border-left: 1px solid #9e9e9e !important;
    border-bottom: 1px solid #9e9e9e !important;
    background: #fff;
    color: black;
    padding: 6px;
    font-size: 15px !important;
  }

  .report-data-header > div:last-child, .report-data > div > div:last-child, .report-data-header > div.last-child, .report-data > div > div.last-child {
    border-right: 1px solid #9e9e9e !important;
  }

  .report-data > div > div > div {
    border-top: none !important;
  }

  .report-data > div > div > div > div {
    border-bottom: 1px solid #9e9e9e !important;
    border-left: 1px solid #9e9e9e !important;
    background: #fff;
    color: #1c1c1c !important;
    padding: 6px;
    font-size: 14px !important;
  }
}

.dot-leader {
  /* Center the content */
  align-items: center;
  display: flex;
  justify-content: center;
}

.dot-leader__dots {
  /* Bottom border */
  border-bottom: 1px dotted #d1d5db;

  /* Take remaining width */
  flex: 1;

  /* Spacing */
  margin: 0 0.25rem;
}

.red-highlighted {
  color: white;
  background-color: red;
  border: 1px solid red;
  padding: 1px 0px 1px 0px;
  display: block;
  padding-left: 5px;
}

.yellow-highlighted {
  color: black;
  background-color: yellow;
  border: 1px solid yellow;
  padding: 1px 0px 1px 0px;
  display: block;
  padding-left: 5px;
}

.blue-highlighted {
  color: white;
  background-color: #1565c0;
  border: 1px solid #1565c0;
  padding: 1px 0px 1px 0px;
  display: block;
  padding-left: 5px;
}

.green-highlighted {
  color: white;
  background-color: green;
  border: 1px solid green;
  padding: 1px 0px 1px 0px;
  display: block;
  padding-left: 5px;
}

.cell-red-highlighted {
  color: white;
  background-color: red;
  border: 1px solid red;
  padding: 1px 0px 1px 0px;
  padding-left: 5px;
}

.cell-yellow-highlighted {
  color: black;
  background-color: yellow;
  border: 1px solid yellow;
  padding: 1px 0px 1px 0px;
  padding-left: 5px;
}

.cell-blue-highlighted {
  color: white;
  background-color: #1565c0;
  border: 1px solid #1565c0;
  padding: 1px 0px 1px 0px;
  padding-left: 5px;
}

.cell-green-highlighted {
  color: white;
  background-color: green;
  border: 1px solid green;
  padding: 1px 0px 1px 0px;
  padding-left: 5px;
}

.red-square {
  height: 25px;
  width: 25px;
  border: 1px solid red;
  background-color: red;
  display: inline-block;
  vertical-align: middle;
}

.yellow-square {
  height: 25px;
  width: 25px;
  border: 1px solid gray;
  background-color: yellow;
  display: inline-block;
  vertical-align: middle;
}

.blue-square {
  height: 25px;
  width: 25px;
  border: 1px solid #1565c0;
  background-color: #1565c0;
  display: inline-block;
  vertical-align: middle;
}

.green-square {
  height: 25px;
  width: 25px;
  border: 1px solid green;
  background-color: green;
  display: inline-block;
  vertical-align: middle;
}

.white-square {
  height: 25px;
  width: 25px;
  border: 2px solid #1565c0;
  background-color: white;
  display: inline-block;
  vertical-align: middle;
}

.closed-deal-button {
  position: inherit;
  margin: auto;
  padding: 7px 10px;
  border-radius: 5px;
  width: 100%;
}

.closed-deal-button-text {
  text-transform: uppercase;
  letter-spacing: 2px;
  background: #58460e;
  -webkit-background-clip: text;
  color: transparent;
  text-shadow: 0 3px 3px rgba(255, 255, 255, 0.5), 0 -1px 1px rgba(0, 0, 0, .3);
}

.gold-background {
  background-color: transparent;
  background-image: url('../assets/goldTexture.jpg') !important;
  animation-name: closed-deal-button;
  animation-duration: 3s;
  animation-delay: 1s;
  animation-iteration-count: infinite;
}

.shine {
  position: relative;
}

.shine button {
  transition: background 0.15s ease-in-out;
  -webkit-transition: background 0.15s ease-in-out;
  -moz-transition: background 0.15s ease-in-out;
  -ms-transition: background 0.15s ease-in-out;
  -o-transition: background 0.15s ease-in-out;
  background: rgba(0, 0, 0, 0.5);
}

.shine i {
  background: -moz-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%);
  background: -webkit-gradient(linear, left top, right top, color-stop(0%, rgba(255, 255, 255, 0)), color-stop(1%, rgba(255, 255, 255, 0.03)), color-stop(30%, rgba(255, 255, 255, 0.85)), color-stop(50%, rgba(255, 255, 255, 0.85)), color-stop(70%, rgba(255, 255, 255, 0.85)), color-stop(71%, rgba(255, 255, 255, 0.85)), color-stop(100%, rgba(255, 255, 255, 0)));
  background: -webkit-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%);
  background: -o-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%);
  background: -ms-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%);
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.6) 30%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.85) 70%, rgba(255, 255, 255, 0.85) 71%, rgba(255, 255, 255, 0) 100%);
  filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#00ffffff", endColorstr="#00ffffff", GradientType=1);
  transform: skew(-10deg, 0deg);
  -webkit-transform: skew(-10deg, 0deg);
  -moz-transform: skew(-10deg, 0deg);
  -ms-transform: skew(-10deg, 0deg);
  -o-transform: skew(-10deg, 0deg);
  animation: shine-movement 3s;
  animation-iteration-count: infinite;
  animation-delay: 1s;
  -webkit-animation: shine-movement 3s;
  -webkit-animation-iteration-count: infinite;
  -webkit-animation-delay: 1s;
  -moz-transform: skew(-10deg, 0deg);
  -moz-animation: shine-movement 3s;
  -moz-animation-iteration-count: infinite;
  -moz-animation-delay: 1s;
  -ms-transform: skew(-10deg, 0deg);
  -ms-animation: shine-movement 3s;
  -ms-animation-iteration-count: infinite;
  -ms-animation-delay: 1s;
  -o-transform: skew(-10deg, 0deg);
  -o-animation: shine-movement 3s;
  -o-animation-iteration-count: infinite;
  -o-animation-delay: 1s;
  position: absolute;
  opacity: 0;
  top: 0;
  left: 0;
  width: 15%;
  height: 100%;
}

@keyframes closed-deal-button {
  0% {
    box-shadow: none;
  }
  50% {
    box-shadow: 0 0 40px -7px #cc9900;
  }
  100% {
    box-shadow: none;
  }
}

@keyframes shine-movement {
  0% {
    left: 0;
    opacity: 0;
  }
  5% {
    opacity: 0;
  }
  48% {
    opacity: 0.2;
  }
  80% {
    opacity: 0;
  }
  100% {
    left: 82%;
  }
}

@-webkit-keyframes shine-movement {
  0% {
    left: 0;
    opacity: 0;
  }
  5% {
    opacity: 0;
  }
  48% {
    opacity: 0.2;
  }
  80% {
    opacity: 0;
  }
  100% {
    left: 82%;
  }
}

@-moz-keyframes shine-movement {
  0% {
    left: 0;
    opacity: 0;
  }
  5% {
    opacity: 0;
  }
  48% {
    opacity: 0.2;
  }
  80% {
    opacity: 0;
  }
  100% {
    left: 88%;
  }
}

@-ms-keyframes shine-movement {
  0% {
    left: 0;
    opacity: 0;
  }
  5% {
    opacity: 0;
  }
  48% {
    opacity: 0.2;
  }
  80% {
    opacity: 0;
  }
  100% {
    left: 82%;
  }
}

@-o-keyframes shine-movement {
  0% {
    left: 0;
    opacity: 0;
  }
  5% {
    opacity: 0;
  }
  48% {
    opacity: 0.2;
  }
  80% {
    opacity: 0;
  }
  100% {
    left: 82%;
  }
}
</style>
